import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { TextField } from '@/components/TextField/TextField';
import { useMediaQuery } from '@/lib/useMediaQuery';

import styles from './SettingsForm.module.css';

export interface SettingsValues {
  firstName: string;
  lastName: string;
  businessName: string;
}

export interface SettingsFormProps {
  email: string;
  initial: SettingsValues;
  pending?: boolean;
  error?: string | null;
  onSave: (values: SettingsValues) => void;
  /** Drawn beside the form on wide screens, e.g. the profile card. */
  aside?: ReactNode;
}

type SectionId = 'personal' | 'business';

/** Below this each section folds into an accordion row. */
const NARROW = '(max-width: 560px)';

function Section({
  id,
  title,
  note,
  collapsible,
  open,
  onToggle,
  children,
}: {
  id: SectionId;
  title: string;
  note: string;
  collapsible: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const expanded = !collapsible || open;
  const intro = (
    <span className={styles.sectionIntro}>
      <span id={`${id}-heading`} className={styles.sectionTitle}>
        {title}
      </span>
      <span className={styles.sectionNote}>{note}</span>
    </span>
  );
  return (
    <section className={styles.section} aria-labelledby={`${id}-heading`}>
      {collapsible ? (
        <h3 className={styles.sectionHeading}>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            onClick={onToggle}
          >
            {intro}
            <span
              className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
              aria-hidden="true"
            >
              ›
            </span>
          </button>
        </h3>
      ) : (
        <h3 className={styles.sectionHeading}>{intro}</h3>
      )}
      {expanded ? children : null}
    </section>
  );
}

function trimmed(values: SettingsValues): SettingsValues {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    businessName: values.businessName.trim(),
  };
}

/** The profile: name and business name. The email is shown, not edited. */
export function SettingsForm({
  email,
  initial,
  pending = false,
  error = null,
  onSave,
  aside,
}: SettingsFormProps) {
  const [values, setValues] = useState(initial);
  const [attempted, setAttempted] = useState(false);
  const narrow = useMediaQuery(NARROW);
  const [open, setOpen] = useState<SectionId | null>('personal');

  const next = trimmed(values);
  const missing = {
    firstName: !next.firstName ? 'Enter your first name.' : undefined,
    lastName: !next.lastName ? 'Enter your last name.' : undefined,
    businessName: !next.businessName ? 'Enter your business name.' : undefined,
  };
  const dirty = (Object.keys(next) as (keyof SettingsValues)[]).some(
    (key) => next[key] !== initial[key],
  );

  function field(key: keyof SettingsValues) {
    return {
      value: values[key],
      error: attempted ? missing[key] : undefined,
      onChange: (event: { target: { value: string } }) =>
        setValues({ ...values, [key]: event.target.value }),
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    if (missing.firstName || missing.lastName) {
      setOpen('personal');
    } else if (missing.businessName) {
      setOpen('business');
    } else {
      onSave(next);
    }
  }

  return (
    <div className={styles.layout}>
      <form className={styles.form} onSubmit={submit} noValidate>
        {error ? <Callout tone="danger">{error}</Callout> : null}

        <Section
          id="personal"
          title="Personal information"
          note="Your name as it appears across the platform."
          collapsible={narrow}
          open={open === 'personal'}
          onToggle={() => setOpen(open === 'personal' ? null : 'personal')}
        >
          <div className={styles.row}>
            <TextField
              label="First name"
              name="given-name"
              autoComplete="given-name"
              required
              {...field('firstName')}
            />
            <TextField
              label="Last name"
              name="family-name"
              autoComplete="family-name"
              required
              {...field('lastName')}
            />
          </div>
          {narrow ? null : (
            <TextField
              label="Email"
              type="email"
              name="email"
              value={email}
              readOnly
              hint="Your email is how you sign in, so it cannot be changed here."
            />
          )}
        </Section>

        <Section
          id="business"
          title="Business"
          note="The business you are building for."
          collapsible={narrow}
          open={open === 'business'}
          onToggle={() => setOpen(open === 'business' ? null : 'business')}
        >
          <TextField
            label="Business name"
            name="organization"
            autoComplete="organization"
            required
            {...field('businessName')}
          />
        </Section>

        <div className={styles.actions}>
          <Button type="submit" loading={pending} disabled={!dirty}>
            Save changes
          </Button>
        </div>
      </form>
      {aside ? <div className={styles.aside}>{aside}</div> : null}
    </div>
  );
}
